import { Routes, Route, Navigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';
import SupplierList from './pages/masters/SupplierList';
import CustomerList from './pages/masters/CustomerList';
import ProductList from './pages/masters/ProductList';
import BrandList from './pages/masters/BrandList';
import ColdStorageList from './pages/masters/ColdStorageList';
import StaffList from './pages/masters/StaffList';
import PurchaseList from './pages/purchases/PurchaseList';
import InventoryChilled from './pages/inventory/InventoryChilled';
import InventoryFrozen from './pages/inventory/InventoryFrozen';
import InventoryByStorage from './pages/inventory/InventoryByStorage';
import ShipmentChilled from './pages/shipments/ShipmentChilled';
import ShipmentFrozen from './pages/shipments/ShipmentFrozen';
import SettlementList from './pages/settlements/SettlementList';
import InvoiceList from './pages/invoices/InvoiceList';
import UserList from './pages/admin/UserList';

// 認証保護されたルート
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--background-main)' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  const [theme, setTheme] = useState<'light' | 'dark'>('light');

  useEffect(() => {
    const saved = localStorage.getItem('theme') as 'light' | 'dark' | null;
    if (saved) {
      setTheme(saved);
      document.documentElement.setAttribute('data-theme', saved);
    }
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    document.documentElement.setAttribute('data-theme', newTheme);
  };

  return (
    <Routes>
      {/* ログイン画面 */}
      <Route path="/login" element={<Login />} />
      
      {/* 保護されたルート */}
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <Layout theme={theme} toggleTheme={toggleTheme}>
              <Routes>
                <Route path="/" element={<Dashboard />} />
                
                {/* マスタ管理 */}
                <Route path="/masters/suppliers" element={<SupplierList />} />
                <Route path="/masters/customers" element={<CustomerList />} />
                <Route path="/masters/products" element={<ProductList />} />
                <Route path="/masters/brands" element={<BrandList />} />
                <Route path="/masters/cold-storages" element={<ColdStorageList />} />
                <Route path="/masters/staff" element={<StaffList />} />
                
                {/* 仕入管理 */}
                <Route path="/purchases" element={<PurchaseList />} />
                
                {/* 在庫管理 */}
                <Route path="/inventory/chilled" element={<InventoryChilled />} />
                <Route path="/inventory/frozen" element={<InventoryFrozen />} />
                <Route path="/inventory/by-storage" element={<InventoryByStorage />} />
                
                {/* 出庫管理 */}
                <Route path="/shipments/chilled" element={<ShipmentChilled />} />
                <Route path="/shipments/frozen" element={<ShipmentFrozen />} />
                
                {/* 精算管理 */}
                <Route path="/settlements" element={<SettlementList />} />
                
                {/* 請求管理 */}
                <Route path="/invoices" element={<InvoiceList />} />
                
                {/* ユーザー管理 */}
                <Route path="/admin/users" element={<UserList />} />
                
                {/* 404 */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Layout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
}

export default App;
